import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { projectService } from "../services/projectService";
import { useAuth } from "./AuthContext";

const ProjectContext = createContext();

export const useProjects = () => {
    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error("useProjects debe usarse dentro de un ProjectProvider");
    }
    return context;
};

export const ProjectProvider = ({ children }) => {
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(true);

    // 1. Obtener todos los proyectos del usuario autenticado
    const fetchProjects = useCallback(async () => {
        if (!user) {
            setProjects([]);
            setLoadingProjects(false);
            return;
        }

        setLoadingProjects(true);
        try {
            let data = await projectService.getProjects();
            const seedKey = `has_seeded_${user.id}`;

            // Si el usuario no tiene proyectos y es su primera vez en la app
            if (data.length === 0 && !localStorage.getItem(seedKey)) {
                localStorage.setItem(seedKey, "true");
                try {
                    const result = await projectService.seedInitialData(user.id);
                    if (result && result.project) {
                        data = [result.project];
                        // Disparar evento para que TaskContext obtenga las tareas de ejemplo de inmediato
                        window.dispatchEvent(new Event("app_seeded"));
                    }
                } catch (seedErr) {
                    console.error("Error al crear datos iniciales de ejemplo:", seedErr);
                }
            }

            setProjects(data);
        } catch (error) {
            console.error("Error al cargar proyectos:", error.message);
        } finally {
            setLoadingProjects(false);
        }
    }, [user]);

    // 2. Crear un nuevo proyecto en Supabase
    const createProject = async (projectData) => {
        try {
            if (!user) throw new Error("No hay sesión de usuario activa.");

            const created = await projectService.createProject(projectData, user.id);
            if (created) {
                setProjects((prev) => [...prev, created]);
            }
            return created;
        } catch (error) {
            console.error("Error al crear proyecto:", error.message);
            throw error;
        }
    };

    // Actualizar un proyecto existente
    const updateProject = async (id, updatedFields) => {
        try {
            if (!user) throw new Error("No hay sesión de usuario activa.");

            const updated = await projectService.updateProject(id, updatedFields, user.id);
            if (updated) {
                setProjects((prev) =>
                    prev.map((proj) => (proj.id === id ? updated : proj))
                );
            }
            return updated;
        } catch (error) {
            console.error("Error al actualizar el proyecto:", error.message);
            throw error;
        }
    };

    // Eliminar un proyecto
    const deleteProject = async (id) => {
        try {
            if (!user) throw new Error("No hay sesión de usuario activa.");

            await projectService.deleteProject(id, user.id);
            setProjects((prev) => prev.filter((proj) => proj.id !== id));
        } catch (error) {
            console.error("Error al eliminar el proyecto:", error.message);
            throw error;
        }
    };

    // Cargar proyectos cuando el usuario cambie
    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    return (
        <ProjectContext.Provider value={{ projects, loadingProjects, createProject, updateProject, deleteProject, fetchProjects }}>
            {children}
        </ProjectContext.Provider>
    );
};